import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useAbortCountSession,
  useCountSessionHistory,
  useCurrentCountSession,
  useOpenCountSession,
} from '../hooks/use-count-session'
import { useCountRestaurantStore } from '../store/count-restaurant-store'
import { formatCorrection } from '../lib/count-item-summary'
import { COUNT_SESSION_STATUS_VARIANT } from '../lib/count-status-variant'
import type { CountSessionHistoryRow } from '@/api/endpoints/inventory-count-sessions'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { ApiError } from '@/api/types'

/**
 * The Inventur entry point: pick the location, start or resume a count, and read what was counted
 * before.
 *
 * Starting used to live on its own screen behind this list, which meant two navigations before the
 * camera on the device the flow is built for. Below `sm` the six-column table becomes cards — the
 * table's columns do not fit an iPhone, and this is where a count begins.
 */
export function CountSessionHistoryPage() {
  const { t } = useTranslation('inventory-count')
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canStartCount = can(ACTIONS.INVENTORY_COUNT_SESSION)
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const restaurantId = useCountRestaurantStore((s) => s.restaurantId)
  const setRestaurantId = useCountRestaurantStore((s) => s.setRestaurantId)

  useEffect(() => {
    if (!restaurantId && restaurants.length > 0) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurants, restaurantId, setRestaurantId])

  const { data, isLoading } = useCountSessionHistory(restaurantId)
  const rows = data?.items ?? []
  const lastFinished =
    rows.find((row) => row.session.status !== 'OPEN')?.session ?? null

  // `current` is `null` once resolved with no open session, `undefined` while not yet resolved —
  // the two branches below key strictly off this and are therefore mutually exclusive by
  // construction (see useCurrentCountSession).
  const { data: current, isLoading: sessionLoading } =
    useCurrentCountSession(restaurantId)
  const open = useOpenCountSession(restaurantId)
  const abort = useAbortCountSession(restaurantId, current?.session.id ?? null)

  function startCounting() {
    open.mutate(undefined, {
      onSuccess: () => navigate({ to: '/inventory/count/capture' }),
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          toast.error(t('start.alreadyOpen'))
        } else {
          toast.error(t('start.openFailed'))
        }
      },
    })
  }

  function abortSession() {
    abort.mutate(undefined, {
      onSuccess: () => toast.success(t('start.aborted')),
      onError: () => toast.error(t('start.abortFailed')),
    })
  }

  function openSession(sessionId: string) {
    navigate({ to: '/inventur/$id', params: { id: sessionId } })
  }

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">{t('history.title')}</h1>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="count-restaurant">
            {t('start.restaurantLabel')}
          </label>
          <Select
            value={restaurantId != null ? String(restaurantId) : undefined}
            onValueChange={(v) => setRestaurantId(Number(v))}
          >
            <SelectTrigger
              id="count-restaurant"
              className="h-12 w-full sm:w-64"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((r) => (
                <SelectItem key={r.id} value={String(r.id)}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/*
          Starting, resuming and aborting live here now: one screen between opening Inventur and
          the camera. Without the permission to run a count the page stays a read-only history.
        */}
        {canStartCount && sessionLoading && restaurantId != null && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {canStartCount && !sessionLoading && current != null && (
          <Card>
            <CardHeader>
              <CardTitle>{t('start.openSessionTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t('start.openSessionBy', {
                  actor: current.session.startedBy,
                  date: new Date(current.session.startedAt).toLocaleString(),
                })}
              </p>
              <Button
                size="lg"
                className="h-12"
                onClick={() => navigate({ to: '/inventory/count/capture' })}
              >
                {t('start.resume')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12"
                disabled={abort.isPending}
                onClick={abortSession}
              >
                {t('start.abort')}
              </Button>
            </CardContent>
          </Card>
        )}

        {canStartCount && !sessionLoading && current === null && (
          <Card>
            <CardContent className="flex flex-col gap-3 pt-6">
              {lastFinished && (
                <p className="text-sm text-muted-foreground">
                  {t('start.lastSession', {
                    status: t(`history.status.${lastFinished.status}`),
                    date: new Date(
                      lastFinished.finishedAt ?? lastFinished.startedAt,
                    ).toLocaleDateString(),
                  })}
                </p>
              )}
              <Button
                size="lg"
                className="h-12"
                disabled={restaurantId == null || open.isPending}
                onClick={startCounting}
              >
                {t('start.begin')}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            {!isLoading && rows.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('history.empty')}
              </p>
            )}

            {(isLoading || rows.length > 0) && (
              <>
                {/* Phone: one card per session, no horizontal scrolling. */}
                <div className="flex flex-col gap-3 sm:hidden">
                  {isLoading
                    ? Array.from({ length: 3 }).map((_, idx) => (
                        <Skeleton key={idx} className="h-20 w-full" />
                      ))
                    : rows.map((row) => (
                        <HistoryCard
                          key={row.session.id}
                          row={row}
                          onOpen={() => openSession(row.session.id)}
                        />
                      ))}
                </div>

                {/* Desktop: the unchanged six-column table. */}
                <div className="hidden overflow-x-auto sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('history.column.date')}</TableHead>
                        <TableHead>{t('history.column.countedBy')}</TableHead>
                        <TableHead>{t('history.column.status')}</TableHead>
                        <TableHead className="text-right">
                          {t('history.column.items')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('history.column.variances')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('history.column.correction')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading
                        ? Array.from({ length: 5 }).map((_, idx) => (
                            <TableRow key={idx}>
                              <TableCell colSpan={6}>
                                <Skeleton className="h-5 w-full" />
                              </TableCell>
                            </TableRow>
                          ))
                        : rows.map((row) => (
                            <HistoryRow
                              key={row.session.id}
                              row={row}
                              onOpen={() => openSession(row.session.id)}
                            />
                          ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}

function HistoryCard({
  row,
  onOpen,
}: {
  row: CountSessionHistoryRow
  onOpen: () => void
}) {
  const { t } = useTranslation('inventory-count')
  const { session, summary } = row

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-11 flex-col gap-1 rounded-lg border border-border p-3 text-left"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {new Date(session.startedAt).toLocaleString()}
        </span>
        <Badge variant={COUNT_SESSION_STATUS_VARIANT[session.status]}>
          {t(`history.status.${session.status}`)}
        </Badge>
      </span>
      <span className="text-xs text-muted-foreground">{session.startedBy}</span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {t('history.card.summary', {
          items: summary.itemCount,
          variances: summary.confirmedVarianceCount,
          correction: formatCorrection(
            summary.correctionUp,
            summary.correctionDown,
          ),
        })}
      </span>
    </button>
  )
}

function HistoryRow({
  row,
  onOpen,
}: {
  row: CountSessionHistoryRow
  onOpen: () => void
}) {
  const { t } = useTranslation('inventory-count')
  const { session, summary } = row

  return (
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell className="font-medium">
        {new Date(session.startedAt).toLocaleString()}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {session.startedBy}
      </TableCell>
      <TableCell>
        <Badge variant={COUNT_SESSION_STATUS_VARIANT[session.status]}>
          {t(`history.status.${session.status}`)}
        </Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {summary.itemCount}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {summary.confirmedVarianceCount}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatCorrection(summary.correctionUp, summary.correctionDown)}
      </TableCell>
    </TableRow>
  )
}
